import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import {
  channelListAtom,
  selectedProductAtom,
  userInformationAtom,
} from '../../../Services/_states/index.js';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import ModalForms from '../../Shared/Modals/index.js';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValue } from 'recoil';

const CreateKnowledgeBasePage = () => {
  const { createKnowledgeBase, listKnowledgeBases, updateKnowledgeBase, deleteKnowledgeBase } =
    AiActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const loggedInUser = useRecoilValue(userInformationAtom);

  // Modal states
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Persist organisation selection in localStorage
  const [organisation, setOrganisation] = useState(() => {
    const saved = localStorage.getItem('selectedOrganisation');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (organisation) {
      localStorage.setItem('selectedOrganisation', JSON.stringify(organisation));
    }
  }, [organisation]);

  // Data states
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [highlightedKB, setHighlightedKB] = useState(null);
  const [selectedKB, setSelectedKB] = useState(null);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);

  const kbRefs = useRef({});

  // Load knowledge bases on component mount
  // useEffect(() => {
  //   loadKnowledgeBases();
  // }, []);

  // Load knowledge bases when organization changes
  // useEffect(() => {
  //   if (organisation?.organisation_id) {
  //     loadKnowledgeBases(organisation.organisation_id);
  //   }
  // }, [organisation]);

  const loadKnowledgeBases = async (orgId = null) => {
    const OrgID = loggedInUser?.profile?.organisation?.organisation_id;
    const organizationId = orgId || organisation?.organisation_id || OrgID;

    setIsLoading(true);
    try {
      const response = await listKnowledgeBases(organizationId);
      if (response && response.knowledge_bases && Array.isArray(response.knowledge_bases)) {
        setKnowledgeBases(
          response.knowledge_bases.map((kb) => ({
            ...kb,
            createdAt: kb.created_at ? new Date(kb.created_at).toLocaleString() : 'N/A',
            updatedAt: kb.updated_at ? new Date(kb.updated_at).toLocaleString() : 'N/A',
          }))
        );
      }
    } catch (error) {
      showSnackBar('Failed to load knowledge bases', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setName('');
    setDescription('');
    setOpen(false);
  };

  const handleEditOpen = (kb) => {
    setSelectedKB(kb);
    setName(kb.name || '');
    setDescription(kb.description || '');
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setName('');
    setDescription('');
    setSelectedKB(null);
    setEditOpen(false);
  };

  const handleDeleteOpen = (kb) => {
    setSelectedKB(kb);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const handleMenuOpen = (event, kb) => {
    event.stopPropagation();
    setSelectedKB(kb);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedKB(null);
  };

  const handleSave = async () => {
    if (!name.trim() || !organisation?.organisation_id) {
      showSnackBar('Please fill all required fields.', 'warning');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      organization_id: organisation?.organisation_id,
    };

    try {
      const response = await createKnowledgeBase(payload);

      if (!response || response?.error || response?.status === false) {
        throw new Error(response?.error?.message || 'Unknown error occurred');
      }

      showSnackBar('Knowledge Base created successfully!', 'success');

      const newKB = {
        ...response,
        createdAt: new Date().toLocaleString(),
      };

      setKnowledgeBases((prev) => [...prev, newKB]);
      setHighlightedKB(newKB.knowledge_base_id);

      setTimeout(() => {
        const ref = kbRefs.current[newKB.knowledge_base_id];
        if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);

      handleClose();
    } catch (error) {
      const errorMessage = error?.message || error?.response?.data?.error?.message;

      if (errorMessage?.includes('max serverless indexes')) {
        showSnackBar(
          "You've reached the max Pinecone index limit (5). Please delete unused indexes, use namespaces, or upgrade your Pinecone plan.",
          'error'
        );
      } else {
        showSnackBar(`Failed to create Knowledge Base. ${errorMessage}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!name.trim()) {
      showSnackBar('Please fill all required fields.', 'warning');
      return;
    }

    if (!selectedKB) {
      showSnackBar('No knowledge base selected for update.', 'error');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
    };

    try {
      const orgId = selectedKB.organization_id || selectedKB.organisation_id;
      const kbId = selectedKB.knowledge_base_id;

      if (!orgId || !kbId) {
        throw new Error('Missing organization ID or knowledge base ID');
      }

      const response = await updateKnowledgeBase(orgId, kbId, payload);

      if (response?.error || response?.status === false) {
        throw new Error(response?.error?.message || response?.message || 'Update failed');
      }

      showSnackBar('Knowledge Base updated successfully!', 'success');

      setKnowledgeBases((prev) =>
        prev.map((kb) =>
          kb.knowledge_base_id === selectedKB.knowledge_base_id
            ? {
                ...kb,
                name: payload.name,
                description: payload.description,
                updatedAt: new Date().toLocaleString(),
              }
            : kb
        )
      );

      setHighlightedKB(selectedKB.knowledge_base_id);
      setTimeout(() => setHighlightedKB(null), 3000);

      handleEditClose();
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'Unknown error occurred';
      showSnackBar(`Failed to update Knowledge Base: ${errorMessage}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (confirmed) => {
    if (!confirmed) {
      setConfirmOpen(false);
      setSelectedKB(null);
      return;
    }

    if (!selectedKB) {
      setConfirmOpen(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const orgId = selectedKB.organization_id || selectedKB.organisation_id;
      const kbId = selectedKB.knowledge_base_id;

      if (!orgId || !kbId) {
        throw new Error('Missing organization ID or knowledge base ID');
      }

      const response = await deleteKnowledgeBase(orgId, kbId);

      if (response?.error || response?.status === false) {
        throw new Error(response?.error?.message || response?.message || 'Delete failed');
      }

      showSnackBar('Knowledge Base deleted successfully!', 'success');

      setKnowledgeBases((prev) =>
        prev.filter((kb) => kb.knowledge_base_id !== selectedKB.knowledge_base_id)
      );
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'Unknown error occurred';
      showSnackBar(`Failed to delete Knowledge Base: ${errorMessage}`, 'error');
    } finally {
      setIsSubmitting(false);
      setConfirmOpen(false);
      setSelectedKB(null);
    }
  };

  // Updated handleViewDetails - removed pre-fetching
  const handleViewDetails = (kb) => {
    const orgId = kb.organization_id || kb.organisation_id;
    const kbId = kb.knowledge_base_id;

    navigate('/r/vector/knowledge-base/details', {
      state: {
        knowledge_base_id: kbId,
        organization_id: orgId,
        knowledgeBase: kb, // Pass the KB data for immediate display
      },
    });
  };

  useEffect(() => {
    if (organisation?.organisation_id || loggedInUser?.profile?.organisation?.organisation_id) {
      const orgId =
        organisation?.organisation_id || loggedInUser?.profile?.organisation?.organisation_id;
      loadKnowledgeBases(orgId);
    }
  }, [organisation, loggedInUser]);

  const modalBody = (
    <>
      <TextField
        label={
          <Typography component="span">
            Name <span style={{ color: 'red' }}>*</span>
          </Typography>
        }
        fullWidth
        size="small"
        variant="outlined"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ my: 1 }}
      />
      <OrganisationDropdown
        value={organisation}
        onChange={setOrganisation}
        label="Organisation"
        sx={{ my: 1 }}
        required
      />
      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        rows={3}
        variant="outlined"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ my: 1 }}
      />
    </>
  );

  const editModalBody = (
    <>
      <TextField
        label={
          <Typography component="span">
            Name <span style={{ color: 'red' }}>*</span>
          </Typography>
        }
        fullWidth
        size="small"
        variant="outlined"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ my: 1 }}
      />
      <OrganisationDropdown
        value={organisation}
        onChange={setOrganisation}
        label="Organisation"
        sx={{ my: 1 }}
      />
      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        rows={3}
        variant="outlined"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ my: 1 }}
      />
    </>
  );

  const modalFooter = (
    <Box display="flex" justifyContent="flex-end" gap={2}>
      <Button onClick={handleClose} variant="outlined" color="secondary">
        Cancel
      </Button>
      <Button onClick={handleSave} variant="contained" disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save'}
      </Button>
    </Box>
  );

  const editModalFooter = (
    <Box display="flex" justifyContent="flex-end" gap={2}>
      <Button onClick={handleEditClose} variant="outlined" color="secondary">
        Cancel
      </Button>
      <Button onClick={handleUpdate} variant="contained" disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Update'}
      </Button>
    </Box>
  );

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate('/'),
    },
    { label: 'Knowledge Bases' },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc' }}>
      <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
        {/* This Box will take 3/4th of the width */}
        <Box sx={{ flex: 3 }}>
          <DynamicBreadcrumbs
            items={breadcrumbsData}
            onBreadcrumbClick={(event, item) => item?.onClick?.()}
          />
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: '#1e293b' }}>
            Knowledge Bases
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Manage your AI knowledge bases and vector embeddings
          </Typography>
        </Box>

        {/* This Box will take 1/4th of the width */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {loggedInUser?.is_superuser && (
            <OrganisationDropdown
              value={organisation}
              onChange={setOrganisation}
              label="Organisation"
              sx={{ my: 1 }}
              required
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={handleOpen}
            sx={{
              height: 240,
              border: '2px dashed #e0e0e0',
              backgroundColor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                backgroundColor: '#f0f4f8',
                borderColor: '#1976d2',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 48, color: '#3b82f6', mb: 2 }} />
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#1e293b' }}>
              Create Knowledge Base
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ fontSize: '0.875rem' }}
            >
              Define KB name and description to get started
            </Typography>
          </Card>
        </Grid>

        {knowledgeBases.map((kb, i) => (
          <Grid item xs={12} sm={6} md={3} key={kb.knowledge_base_id || i}>
            <Card
              ref={(el) => (kbRefs.current[kb.knowledge_base_id] = el)}
              elevation={0}
              sx={{
                height: 240,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: 2.5,
                backgroundColor: kb.knowledge_base_id === highlightedKB ? '#f0f9ff' : 'white',
                transition: 'all 0.3s ease',
                border:
                  kb.knowledge_base_id === highlightedKB
                    ? '2px solid #3b82f6'
                    : '1px solid #e2e8f0',
                borderRadius: 2,
                position: 'relative',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                  transform: 'translateY(-2px)',
                  borderColor: '#cbd5e1',
                },
              }}
            >
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ flex: 1, mr: 1, fontSize: '1.1rem', color: '#1e293b' }}
                  >
                    {kb.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: kb.status === 'active' ? '#dcfce7' : '#fef3c7',
                        color: kb.status === 'active' ? '#166534' : '#92400e',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {kb.status}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, kb)}
                      sx={{
                        p: 0.5,
                        '&:hover': { bgcolor: '#f1f5f9' },
                      }}
                    >
                      <MoreVertIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                  </Box>
                </Box>

                {kb.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5, mb: 1.5, fontSize: '0.875rem', lineHeight: 1.4 }}
                  >
                    {kb.description.length > 45
                      ? `${kb.description.substring(0, 45)}...`
                      : kb.description}
                  </Typography>
                )}

                {/* Statistics */}
                {kb.statistics && (
                  <Box
                    sx={{
                      mt: 1.5,
                      px: 1.2,
                      py: 0.5,
                      bgcolor: '#f8fafc',
                      borderRadius: 1,
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'nowrap',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography sx={{ color: '#3b82f6', fontWeight: 600, fontSize: '0.8rem' }}>
                        {kb.statistics.total_files}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        Files
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography sx={{ color: '#8b5cf6', fontWeight: 600, fontSize: '0.8rem' }}>
                        {kb.statistics.total_chunks}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        Chunks
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography sx={{ color: '#10b981', fontWeight: 600, fontSize: '0.8rem' }}>
                        {(kb.statistics.total_size / 1024).toFixed(1)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        KB
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Model: {kb.embedding_model || 'N/A'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Index: {kb.index_name ? kb.index_name.substring(0, 20) + '...' : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 0.5, mt: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <CalendarTodayIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {kb.createdAt}
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleViewDetails(kb)}
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#3b82f6',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: '#1d4ed8',
                    },
                  }}
                >
                  View Details →
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            const kbToEdit = selectedKB;
            handleMenuClose();
            handleEditOpen(kbToEdit);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            const kbToDelete = selectedKB;
            handleMenuClose();
            handleDeleteOpen(kbToDelete);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Modal */}
      <ModalForms
        show={open}
        handleClose={handleClose}
        modalHeader="Create Knowledge Base"
        modalBody={modalBody}
        modalFooter={modalFooter}
        size="md"
        modalId="create-knowledge-base-modal"
      />

      {/* Edit Modal */}
      <ModalForms
        show={editOpen}
        handleClose={handleEditClose}
        modalHeader="Edit Knowledge Base"
        modalBody={editModalBody}
        modalFooter={editModalFooter}
        size="md"
        modalId="edit-knowledge-base-modal"
      />

      {/* Delete Confirmation */}
      <ConfirmBox
        open={confirmOpen}
        onClose={handleDelete}
        options={{
          title: 'Delete Knowledge Base',
          text: `Are you sure you want to delete "${selectedKB?.name || 'this knowledge base'}"? This action cannot be undone and will permanently remove all associated data.`,
          confirmButtonText: isSubmitting ? 'Deleting...' : 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'secondary',
        }}
      />
    </Box>
  );
};

export default CreateKnowledgeBasePage;
